"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useAuth } from "@/contexts/auth-context";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { PageHeader } from "@/components/layout/page-header";
import { SidebarDrawer, useDrawer } from "@/components/layout/sidebar-drawer";
import { ModuleCard } from "@/components/home/module-card";
import { AttentionItems } from "@/components/home/attention-items";
import { CalendarCard } from "@/components/home/calendar-card";
import { UrgentAvisosBanner } from "@/components/home/urgent-avisos-banner";
import { ReservationDetailModal } from "@/components/agenda/reservation-detail-modal";
import { EventDetailModal } from "@/components/agenda/event-detail-modal";
import { AvisoDetailSheet } from "@/components/agenda/aviso-detail-sheet";
import { toModuleMetadata } from "@/config/modules";
import { useManualUpdates } from "@/hooks/use-manual";
import { useAttentionItems } from "@/hooks/use-attention-items";
import { useAvisos } from "@/hooks/use-avisos";
import type { Event } from "@/types/events";
import type { Reservation } from "@/types/reservations";
import type { Aviso } from "@/types/avisos";
import { apiFetch } from "@/lib/api-client";

const WEEKDAYS_LONG = ["Domingo","Segunda-feira","Terça-feira","Quarta-feira","Quinta-feira","Sexta-feira","Sábado"];
const MONTHS_LONG = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Bom dia";
  if (hour >= 12 && hour < 18) return "Boa tarde";
  return "Boa noite";
}

function getGreetingSubtitle(): string {
  const now = new Date();
  return `${WEEKDAYS_LONG[now.getDay()]}, ${now.getDate()} de ${MONTHS_LONG[now.getMonth()]}`;
}

function SectionHeader({
  title,
  right,
}: {
  title: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-2.5 flex items-center justify-between">
      <h2 className="text-11 font-bold uppercase tracking-[0.1em] text-muted-foreground/70">
        {title}
      </h2>
      {right}
    </div>
  );
}

function HomePage() {
  const { user, permissions, hasModuleAccess } = useAuth();
  const { isOpen, toggle, close } = useDrawer();
  const [events, setEvents] = useState<Event[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loadingAgenda, setLoadingAgenda] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedAviso, setSelectedAviso] = useState<Aviso | null>(null);

  const greeting = getGreeting();
  const firstName = user?.name.split(" ")[0] || "";
  const accessibleModules = permissions?.homeModules.map(toModuleMetadata) ?? [];
  const { data: manualUpdates } = useManualUpdates();
  const { items: attentionItems, loading: attentionLoading } = useAttentionItems();
  const { avisos, arquivarAviso } = useAvisos();
  const urgentAvisos = avisos.filter((a) => a.urgente);

  useEffect(() => {
    if (!hasModuleAccess("agenda")) {
      setLoadingAgenda(false);
      setEvents([]);
      setReservations([]);
      return;
    }
    fetchAgendaData();
  }, [hasModuleAccess]);

  const fetchAgendaData = async () => {
    setLoadingAgenda(true);
    try {
      const [eventsRes, reservationsRes] = await Promise.all([
        apiFetch("/api/v1/events"),
        apiFetch("/api/v1/reservations"),
      ]);
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData.data || []);
      }
      if (reservationsRes.ok) {
        const reservationsData = await reservationsRes.json();
        setReservations(reservationsData.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch agenda data:", err);
    } finally {
      setLoadingAgenda(false);
    }
  };

  function getBadge(moduleId: string): number | undefined {
    if (moduleId === "manual") return manualUpdates?.unread_count;
    return undefined;
  }

  return (
    <div className="min-h-dvh bg-background">
      <SidebarDrawer open={isOpen} onClose={close} />
      <PageHeader title="Início" onMenuClick={toggle} />

      <main className="px-4 pb-14 pt-1 space-y-6">
        {/* ── Greeting ── */}
        <section>
          <h1 className="text-2xl font-bold leading-tight tracking-tight text-foreground">
            {greeting}, {firstName}!
          </h1>
          <p className="mt-1 text-13 text-muted-foreground">
            {getGreetingSubtitle()}
          </p>
        </section>

        {/* ── Avisos urgentes ── */}
        {urgentAvisos.length > 0 && (
          <section>
            <SectionHeader
              title="Avisos"
              right={
                <span className="rounded-full bg-[#ff6b2b] px-2 py-0.5 text-11 font-bold leading-tight text-white">
                  {urgentAvisos.length}
                </span>
              }
            />
            <UrgentAvisosBanner avisos={urgentAvisos} onDismiss={arquivarAviso} />
          </section>
        )}

        {/* ── Agenda ── */}
        {hasModuleAccess("agenda") && (
          <section>
            <SectionHeader
              title="Agenda"
              right={
                <Link
                  href="/agenda/calendario"
                  className="text-xs font-semibold text-primary"
                >
                  Ver tudo →
                </Link>
              }
            />
            <CalendarCard
              events={events}
              reservations={reservations}
              avisos={avisos}
              loading={loadingAgenda}
              onEventClick={setSelectedEvent}
              onReservationClick={setSelectedReservation}
              onAvisoClick={setSelectedAviso}
            />
          </section>
        )}

        {/* ── Alertas do sistema ── */}
        {!attentionLoading && attentionItems.length > 0 && (
          <section>
            <SectionHeader
              title="Alertas"
              right={
                <span className="rounded-full bg-[#ff6b2b] px-2 py-0.5 text-11 font-bold leading-tight text-white">
                  {attentionItems.length}
                </span>
              }
            />
            <AttentionItems items={attentionItems} loading={attentionLoading} />
          </section>
        )}

        {/* ── Módulos ── */}
        <section>
          <SectionHeader title="Módulos" />
          <div className="grid auto-rows-fr grid-cols-3 gap-2">
            {accessibleModules.map((module, i) => {
              const isLast = i === accessibleModules.length - 1;
              const remainder = accessibleModules.length % 3;
              const span: 1 | 2 | 3 = isLast && remainder === 1
                ? 3
                : isLast && remainder === 2
                  ? 2
                  : 1;
              return (
                <ModuleCard
                  key={module.id}
                  module={module}
                  badge={getBadge(module.id)}
                  span={span}
                />
              );
            })}
          </div>
        </section>

        {accessibleModules.length === 0 && (
          <div className="rounded-2xl border border-border bg-card px-4 py-12 text-center">
            <p className="text-lg text-muted-foreground">
              Você não tem acesso a nenhum módulo no momento.
            </p>
          </div>
        )}
      </main>

      {selectedReservation && (
        <ReservationDetailModal
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
          onEdit={() => {}}
          onDelete={() => {}}
          deleting={false}
        />
      )}

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onEdit={() => {}}
          onDelete={() => {}}
          deleting={false}
        />
      )}

      {selectedAviso && (
        <AvisoDetailSheet aviso={selectedAviso} onClose={() => setSelectedAviso(null)} />
      )}
    </div>
  );
}

export default function Home() {
  return (
    <ProtectedRoute>
      <HomePage />
    </ProtectedRoute>
  );
}
